import {useState, useRef, useEffect, useMemo} from "react";
import moment from "moment";

export const useSearchQueryGenerator = (options, onChange) => {
  const [inputValue, setInputValue] = useState("");
  const [tokens, setTokens] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownStage, setDropdownStage] = useState("category");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const isDateType = selectedCategory?.type === "date";

  const resetDropdownState = () => {
    setDropdownStage("category");
    setSelectedCategory(null);
    setSelectedOperator(null);
    setInputValue("");
    setSelectedDate(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
        resetDropdownState();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    if (dropdownStage === "category") {
      return options.filter((opt) => {
        const matchesInput = opt.category.label
          .toLowerCase()
          .includes(inputValue.toLowerCase());
        const isDuplicate = tokens.find(
          (token) => token.category.value === opt.category.value
        );

        if (opt.isAllowDuplicateCategories) {
          return matchesInput;
        } else {
          return matchesInput && !isDuplicate;
        }
      });
    }
    if (dropdownStage === "operator") {
      return selectedCategory?.operator || [];
    }
    if (dropdownStage === "value") {
      return (selectedCategory?.options || []).filter((opt) =>
        opt.label.toLowerCase().includes(inputValue.toLowerCase())
      );
    }
    return [];
  }, [inputValue, dropdownStage, selectedCategory, options, tokens]);

  const handleInputFocus = () => {
    if (!showDropdown) {
      setShowDropdown(true);
      setDropdownStage("category");
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleCategorySelect = (option) => {
    setSelectedCategory(option);
    setDropdownStage("operator");
    setInputValue("");
  };

  const handleOperatorSelect = (operator) => {
    setSelectedOperator(operator);
    setDropdownStage("value");
    setInputValue("");
    inputRef.current?.focus();
  };

  const handleValueSelect = (value) => {
    const newToken = {
      id: Date.now(),
      category: selectedCategory.category,
      operator: selectedOperator,
      value,
    };
    const updatedTokens = [...tokens, newToken];
    setTokens(updatedTokens);
    onChange(updatedTokens);
    resetDropdownState();
    inputRef.current?.focus();
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleDateConfirm = () => {
    if (!selectedDate) return;
    const formatted = moment(selectedDate).format("YYYY-MM-DD");
    handleValueSelect({label: formatted, value: formatted});
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && inputValue) {
      if (dropdownStage === "value") {
        if (selectedCategory?.isAllowCustomValue) {
          handleValueSelect({label: inputValue, value: inputValue});
        } else if (filteredOptions.length === 1) {
          handleValueSelect(filteredOptions[0]);
        }
      } else if (dropdownStage === "category" && filteredOptions.length === 1) {
        handleCategorySelect(filteredOptions[0]);
      }
    } else if (e.key === "Backspace" && !inputValue && tokens.length > 0) {
      removeToken(tokens[tokens.length - 1].id);
    } else if (e.key === "Escape") {
      setShowDropdown(false);
      resetDropdownState();
      inputRef.current?.blur();
    }
  };

  const removeToken = (tokenId) => {
    const updatedTokens = tokens.filter((t) => t.id !== tokenId);
    setTokens(updatedTokens);
    onChange(updatedTokens);
  };

  const clearAll = () => {
    setTokens([]);
    onChange([]);
    resetDropdownState();
  };

  return {
    clearAll,
    dropdownStage,
    filteredOptions,
    handleCategorySelect,
    handleDateConfirm,
    handleDateSelect,
    handleInputChange,
    handleInputFocus,
    handleKeyDown,
    handleOperatorSelect,
    handleValueSelect,
    inputRef,
    inputValue,
    isDateType,
    removeToken,
    selectedCategory,
    selectedDate,
    showDropdown,
    tokens,
    wrapperRef,
  };
};
